import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiRefreshCw, FiSunrise, FiPlay, FiAlertTriangle, FiDatabase, FiSettings, FiArrowRight } from 'react-icons/fi';
import * as api from '../api/api';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const pct = (v) => v == null ? '—' : `${(Number(v) * 100).toFixed(1)}%`;
const num = (v, digits=1) => v == null ? '—' : Number(v).toFixed(digits);
const fmtTime = (value) => {
  if (!value) return 'Not run';
  try { return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
  catch (_) { return '—'; }
};

const StatusChip = ({ label, value, tone='good' }) => (
  <div className="status-chip"><span className={`status-dot ${tone}`}/><span>{label}</span><strong>{value}</strong></div>
);

const MarketIntelligence = () => {
  const navigate = useNavigate();
  const localExecution = process.env.REACT_APP_LOCAL_EXECUTION === 'true';
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [latest, setLatest] = useState(null);

  const refresh = async () => {
    try {
      const [s, l] = await Promise.all([api.getIntelligenceStatus(), api.getIntelligenceLatest()]);
      setStatus(s.data);
      setLatest(l.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load Intelligence.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, 15000);
    return () => clearInterval(timer);
  }, []);

  const run = (request, label, success) => toast.promise(request(), {
    loading: label,
    success: () => { refresh(); return success; },
    error: (e) => e.response?.data?.detail || 'Scan failed.'
  });

  const daily = latest?.daily;
  const regime = daily?.regime;
  const leaders = daily?.scan?.leaders || [];
  const openingSnapshot = latest?.opening?.opening || {};
  const opening = openingSnapshot.stocks_in_play || [];
  const themes = useMemo(
    () => Object.values(latest?.themes?.themes || {}).sort((a,b) => Number(b.heat||0)-Number(a.heat||0)),
    [latest]
  );
  const sectors = regime?.hot_sectors || [];
  const synthetic = Boolean(status?.synthetic_data || daily?.synthetic_data || latest?.opening?.synthetic_data);
  const provenance = latest?.provenance || {};
  const partialData = String(regime?.label || '') === 'DATA_INCOMPLETE';
  const unknownInputs = regime?.coverage?.unknown_inputs || [];
  const busy = Boolean(status?.running_job);

  const themeForTicker = (ticker) => themes.find(t => (t.tickers || []).includes(ticker));

  if (loading) return <div className="workspace-page"><LoadingSpinner /></div>;

  const syntheticUiAllowed = process.env.REACT_APP_ALLOW_SYNTHETIC_UI === 'true';
  const realDataReady = Boolean(status?.configured && (!status?.synthetic_data || syntheticUiAllowed));

  if (!realDataReady) {
    return (
      <div className="workspace-page">
        <div className="page-header">
          <div className="page-header-copy">
            <div className="eyebrow">Discovery engine</div>
            <h1 className="page-title">{localExecution ? 'Market Intelligence' : 'AlgoStock Scanner'}</h1>
            <p className="page-description">{localExecution ? 'Real market data is required before AlgoStock can produce market intelligence.' : 'Ο AlgoStock Scanner οργανώνει market regime, themes, leaders και opening confirmation.'}</p>
          </div>
        </div>

        <div className="real-data-gate">
          <div className="real-data-icon"><FiDatabase/></div>
          <div className="real-data-copy">
            <span className="gate-kicker">MARKET DATA</span>
            <h2>{localExecution ? 'No provider connected' : 'Market service unavailable'}</h2>
            <p>
              {localExecution
                ? `${status?.synthetic_data ? 'Synthetic development data is active in the backend but hidden from the product UI. ' : ''}Connect a market-data provider, validate the feed, then run the full pipeline.`
                : 'Market data is temporarily unavailable. Please try again shortly.'}
            </p>
            <div className="gate-actions">
              {localExecution && <button className="ws-button primary" onClick={()=>navigate('/workspace-settings')}><FiSettings/> Open Market Data Settings</button>}
              <button className={`ws-button ${localExecution ? '' : 'primary'}`} onClick={refresh}><FiRefreshCw/> Check again</button>
            </div>
          </div>
          <div className="gate-status">
            <div><span>Delivery</span><strong>{localExecution ? 'LOCAL' : 'ALGOSTOCK CLOUD'}</strong></div>
            <div><span>Market data</span><strong className="warn">UNAVAILABLE</strong></div>
            {localExecution && <div><span>Broker</span><strong className="good">OPTIONAL</strong></div>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="workspace-page">
      <div className="page-header">
        <div className="page-header-copy">
          <div className="eyebrow">Discovery engine</div>
          <h1 className="page-title">{localExecution ? 'Market Intelligence' : 'AlgoStock Scanner'}</h1>
          <p className="page-description">Regime → themes → momentum leaders → opening confirmation. Use this screen to decide what deserves attention today.</p>
        </div>
        <div className="page-actions">
          {!localExecution && <button className="ws-button" onClick={refresh}><FiRefreshCw/> Refresh snapshot</button>}
          {localExecution && <>
          <button className="ws-button" disabled={!status?.configured || busy} onClick={() => run(api.runIntelligenceScanner, 'Scanning US momentum universe…', 'Daily scan complete.')}><FiRefreshCw/> Daily</button>
          <button className="ws-button" disabled={!status?.configured || busy} onClick={() => run(api.runOpeningScanner, 'Scanning opening RVOL…', 'Opening scan complete.')}><FiSunrise/> Opening</button>
          <button className="ws-button primary" disabled={!status?.configured || busy} onClick={() => run(api.runFullIntelligencePipeline, 'Running full Intelligence pipeline…', synthetic ? 'Synthetic pipeline complete.' : 'Intelligence pipeline complete.')}><FiPlay/> Run full scan</button></>}
        </div>
      </div>

      <div className="status-strip">
        <StatusChip label="Data" value={localExecution ? (status?.provider || 'NONE') : (partialData ? 'REAL · PARTIAL' : 'REAL')} tone={(synthetic || partialData) ? 'warn' : 'good'} />
        <StatusChip label="Daily" value={fmtTime(status?.last_daily_scan_at)} tone={status?.last_daily_scan_at ? 'good' : 'warn'} />
        <StatusChip label="Opening" value={fmtTime(status?.last_opening_scan_at)} tone={status?.last_opening_scan_at ? 'good' : 'warn'} />
        <StatusChip label="Automation" value={status?.automation_enabled ? 'ON' : 'OFF'} tone={status?.automation_enabled ? 'good' : 'warn'} />
        <StatusChip label="Session" value={status?.calendar?.latest_session || '—'} tone="good" />
      </div>

      {synthetic && (
        <div className="ws-banner warning"><FiAlertTriangle/><div><strong>Demo data active.</strong> Rankings, prices and signals are synthetic and exist only to validate the product flow.</div></div>
      )}
      {partialData && (
        <div className="ws-banner warning"><FiAlertTriangle/><div><strong>Partial real-data coverage.</strong> Full AlgoStock scores and leader promotion are disabled until required inputs are available{unknownInputs.length ? `: ${unknownInputs.join(', ')}` : '.'}</div></div>
      )}
      {(provenance.daily_stale || provenance.opening_stale || provenance.stale_auto_themes_filtered > 0) && (
        <div className="ws-banner warning"><FiAlertTriangle/><div>{localExecution ? <><strong>Provider changed.</strong> Old snapshots were hidden. Run the full scan to rebuild a clean dataset.</> : <><strong>Market data is updating.</strong> Please refresh shortly.</>}</div></div>
      )}
      {status?.last_error && <div className="ws-banner danger">{localExecution ? <><strong>Last scan error:</strong> {status.last_error}</> : 'Market update temporarily unavailable. Please try again later.'}</div>}

      <div className="ws-grid two">
        <section className="ws-panel pad">
          <div className="section-header"><h2 className="section-title">Market Regime</h2><span className="section-meta">Risk context</span></div>
          {regime ? <>
            <div className="regime-hero">
              <div>
                <div className="regime-name">{String(regime.label || '—').replaceAll('_',' ')}</div>
                <div className="row-sub">{regime.risk_posture || '—'} · research size {regime.research_risk_multiplier ?? '—'}R</div>
              </div>
              <div className="regime-score">{num(regime.score,0)}<small>/100 research</small></div>
            </div>
            <div className="component-list">
              {Object.entries(regime.components || {}).map(([key,value]) => (
                <div className="component-row" key={key}>
                  <span>{key.replaceAll('_',' ')}</span>
                  <div className="component-track"><div className="component-fill" style={{width:`${Math.max(0,Math.min(100,Number(value)||0))}%`}}/></div>
                  <strong>{num(value,0)}</strong>
                </div>
              ))}
            </div>
          </> : <div className="empty-state"><strong>No regime snapshot</strong>Run the daily scan to build market context.</div>}
        </section>

        <section className="ws-panel pad">
          <div className="section-header"><h2 className="section-title">Stocks in Play</h2><span className="section-meta">Candidates · RVOL ≥ {num(openingSnapshot.min_rvol5,1)}x · plan required</span></div>
          {opening.length ? <div className="compact-list">
            {opening.slice(0,6).map(row => (
              <div className="compact-row" key={row.symbol}>
                <button className="ticker-link" aria-label={`Inspect ${row.symbol}`} onClick={()=>navigate('/today?ticker=' + encodeURIComponent(row.symbol))}>{row.symbol}</button>
                <div className="row-main"><div className="row-title">{(row.preferred_setup || 'No setup').replaceAll('_',' ')} candidate</div><div className="row-sub">{row.theme || themeForTicker(row.symbol)?.theme || row.sector || 'No confirmed theme'} · {row.decision_state || 'PLAN_REQUIRED'}</div></div>
                <span className={`value-pill ${Number(row.rvol5)>=2 ? 'good':'warn'}`}>{num(row.rvol5,2)}x RVOL</span>
                <span className="value-pill">{num(row.preferred_setup_score,0)} heuristic</span>
                <button className="terminal-link" aria-label={`Plan ${row.symbol}`} onClick={()=>navigate('/trade-plans?ticker=' + encodeURIComponent(row.symbol))}>Plan <FiArrowRight/></button>
              </div>
            ))}
          </div> : <div className="empty-state"><strong>No opening names yet</strong>{openingSnapshot.observed_count ? `${openingSnapshot.observed_count} candidates checked; none cleared the RVOL gate.` : 'Run the opening scan after the first five minutes.'}</div>}
        </section>
      </div>

      <div className="section-stack">
        <div className="ws-grid two">
          <section className="ws-panel pad">
            <div className="section-header"><h2 className="section-title">Sector Leadership</h2><span className="section-meta">Relative strength</span></div>
            {sectors.length ? <div className="ws-table-wrap"><table className="ws-table"><thead><tr><th>ETF</th><th className="number">5D</th><th className="number">20D</th><th className="number">60D</th><th className="number">RS/SPY</th><th className="number">Trend</th></tr></thead><tbody>
              {sectors.slice(0,10).map(row => <tr key={row.etf}><td><strong>{row.etf}</strong></td><td className="number">{pct(row.return_5d)}</td><td className="number">{pct(row.return_20d)}</td><td className="number">{pct(row.return_60d)}</td><td className="number">{pct(row.rs_spy_20d)}</td><td className="number">{num(row.trend_score,0)}</td></tr>)}
            </tbody></table></div> : <div className="empty-state">No sector snapshot.</div>}
          </section>

          <section className="ws-panel pad">
            <div className="section-header"><h2 className="section-title">Theme Radar</h2><span className="section-meta">Market structure + catalyst overlay</span></div>
            {themes.length ? <div className="compact-list">
              {themes.slice(0,7).map(t => <div className="compact-row" key={t.theme}>
                <div className="value-pill good">{num(t.heat,0)}</div>
                <div className="row-main"><div className="row-title">{t.theme}</div><div className="row-sub">{(t.tickers||[]).slice(0,5).join(' · ') || 'No tickers'}</div></div>
                <span className="value-pill">{t.label || '—'}</span>
                <span className="value-pill">{t.acceleration?.state || 'STABLE'}</span>
              </div>)}
            </div> : <div className="empty-state"><strong>No confirmed themes</strong>Theme candidates appear from industry/sector leadership; news and catalysts enrich the structure when available.</div>}
          </section>
        </div>

        <section className="ws-panel pad">
          <div className="section-header"><h2 className="section-title">Momentum Leaders</h2><span className="section-meta">Hard-gated daily shortlist · {leaders.length} names</span></div>
          {leaders.length ? <div className="ws-table-wrap"><table className="ws-table"><thead><tr><th>#</th><th>Ticker</th><th className="number">AlgoStock Score</th><th>Tier</th><th>Evidence</th><th className="number">Coverage</th><th className="number">1M pct</th><th className="number">3M pct</th><th className="number">ATR%</th><th className="number">From 52W high</th><th>Industry</th><th className="number">Group %ile</th><th>Sector</th><th>Theme</th></tr></thead><tbody>
            {leaders.slice(0,30).map((row,i) => <tr key={row.symbol}><td>{i+1}</td><td><button className="ticker-link" aria-label={`Inspect ${row.symbol}`} onClick={()=>navigate('/today?ticker=' + encodeURIComponent(row.symbol))}>{row.symbol}</button></td><td className="number"><strong>{num(row.algostock_score,1)}</strong></td><td>{row.algostock_score_tier || '—'}</td><td>{row.algostock_score_detail?.evidence_state || '—'}</td><td className="number">{row.algostock_score_detail?.coverage == null ? '—' : Math.round(Number(row.algostock_score_detail.coverage) * 100) + '%'}</td><td className="number">{num(row.momentum_percentile_1m,0)}</td><td className="number">{num(row.momentum_percentile_3m,0)}</td><td className="number">{pct(row.atr_pct)}</td><td className="number">{pct(row.distance_52w_high)}</td><td>{row.industry || '—'}</td><td className="number">{num(row.industry_percentile ?? row.sector_percentile,0)}</td><td>{row.sector || '—'}</td><td>{themeForTicker(row.symbol)?.theme || '—'}</td></tr>)}
          </tbody></table></div> : <div className="empty-state"><strong>No daily leaders</strong>Run the full scan to produce the shortlist.</div>}
        </section>
      </div>
    </div>
  );
};

export default MarketIntelligence;
